import React, { useState, useEffect } from 'react';
import { 
  getAllSystemPrompts, 
  createSystemPromptVersion, 
  activateSystemPromptVersion,
  updateSystemPrompt,
  deleteSystemPrompt,
  duplicateSystemPrompt,
  getSystemPromptById
} from '../../utils/systemPromptService';
import { checkAdminAccess, requireAdminAccess } from '../../utils/adminService';
import { Plus, Save, X, Shield, Copy, Terminal, Eye, Edit, Trash2, CheckCircle } from 'lucide-react';
import { AdminLayout, AdminAccessDenied } from '../../components/admin/layout';

const AdminPromptManager = () => {
  const [prompts, setPrompts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPrompt, setSelectedPrompt] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [editingPrompt, setEditingPrompt] = useState(null);
  const [showDuplicateForm, setShowDuplicateForm] = useState(false);
  const [duplicatingPrompt, setDuplicatingPrompt] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [accessDenied, setAccessDenied] = useState(false);
  const [newPrompt, setNewPrompt] = useState({
    version: '',
    content: '',
    description: '',
    targetUserGroup: 'all',
    consultantType: 'latte-manager'
  });
  const [duplicateVersion, setDuplicateVersion] = useState('');

  useEffect(() => {
    checkAccess();
  }, []);

  const checkAccess = async () => {
    try {
      const adminAccess = await checkAdminAccess();
      setIsAdmin(adminAccess);
      
      if (adminAccess) {
        await loadPrompts();
      } else {
        setAccessDenied(true);
        setLoading(false);
      }
    } catch (error) {
      console.error('권한 확인 실패:', error);
      setAccessDenied(true);
      setLoading(false);
    }
  };

  const loadPrompts = async () => {
    try {
      // 관리자 권한 재확인
      await requireAdminAccess();
      
      const data = await getAllSystemPrompts();
      setPrompts(data);
    } catch (error) {
      console.error('프롬프트 로딩 실패:', error);
      if (error.message?.includes('Admin privileges required')) {
        setAccessDenied(true);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePrompt = async (e) => {
    e.preventDefault();
    try {
      // 관리자 권한 확인
      await requireAdminAccess();
      
      await createSystemPromptVersion(
        'latteis_main',
        newPrompt.version,
        newPrompt.content,
        newPrompt.description,
        newPrompt.targetUserGroup,
        newPrompt.consultantType
      );
      
      setNewPrompt({ version: '', content: '', description: '', targetUserGroup: 'all', consultantType: 'latte-manager' });
      setShowCreateForm(false);
      await loadPrompts();
      alert('새 프롬프트 버전이 생성되었습니다!');
    } catch (error) {
      console.error('프롬프트 생성 실패:', error);
      if (error.message?.includes('Admin privileges required')) {
        alert('관리자 권한이 필요합니다.');
      } else {
        alert('프롬프트 생성에 실패했습니다.');
      }
    }
  };

  const handleActivatePrompt = async (promptId) => {
    if (window.confirm('이 프롬프트 버전을 활성화하시겠습니까?\n\n⚠️ 현재 진행 중인 모든 AI 대화 세션이 재시작됩니다.')) {
      try {
        await activateSystemPromptVersion(promptId, 'latteis_main');
        
        // 프롬프트 변경 시 AI 세션 리셋
        try {
          const { forceResetSessionForPromptChange } = await import('../../utils/geminiAPI_SDK');
          forceResetSessionForPromptChange();
        } catch (importError) {
          console.error('geminiAPI_SDK import 실패:', importError);
          // import 실패해도 프롬프트 활성화는 계속 진행
        }
        
        await loadPrompts();
        alert('프롬프트가 활성화되었습니다!\n새로운 대화부터 변경된 프롬프트가 적용됩니다.');
      } catch (error) {
        console.error('프롬프트 활성화 실패:', error);
        alert('프롬프트 활성화에 실패했습니다.');
      }
    }
  };

  const handleUpdatePrompt = async (e) => {
    e.preventDefault();
    try {
      const updates = {
        version: editingPrompt.version,
        content: editingPrompt.content,
        description: editingPrompt.description,
        target_user_group: editingPrompt.target_user_group,
        consultant_type: editingPrompt.consultant_type
      };
      
      await updateSystemPrompt(editingPrompt.id, updates);
      
      // 활성화된 프롬프트가 수정된 경우 AI 세션 리셋
      if (editingPrompt.is_active) {
        try {
          const { forceResetSessionForPromptChange } = await import('../../utils/geminiAPI_SDK');
          forceResetSessionForPromptChange();
        } catch (importError) {
          console.error('geminiAPI_SDK import 실패:', importError);
        }
        alert('프롬프트가 업데이트되었습니다!\n활성 프롬프트가 수정되어 AI 세션이 재시작됩니다.');
      } else {
        alert('프롬프트가 업데이트되었습니다!');
      }
      
      setShowEditForm(false);
      setEditingPrompt(null);
      await loadPrompts();
    } catch (error) {
      console.error('프롬프트 업데이트 실패:', error);
      alert('프롬프트 업데이트에 실패했습니다.');
    }
  };

  const handleDeletePrompt = async (promptId, version) => {
    if (window.confirm(`프롬프트 버전 "${version}"을 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.`)) {
      try {
        await deleteSystemPrompt(promptId);
        await loadPrompts();
        alert('프롬프트가 삭제되었습니다.');
      } catch (error) {
        console.error('프롬프트 삭제 실패:', error);
        alert('프롬프트 삭제에 실패했습니다.');
      }
    }
  };

  const handleDuplicatePrompt = async (e) => {
    e.preventDefault();
    try {
      await duplicateSystemPrompt(duplicatingPrompt.id, duplicateVersion);
      setShowDuplicateForm(false);
      setDuplicatingPrompt(null);
      setDuplicateVersion('');
      await loadPrompts();
      alert('프롬프트가 복제되었습니다!');
    } catch (error) {
      console.error('프롬프트 복제 실패:', error);
      alert('프롬프트 복제에 실패했습니다.');
    }
  };

  const startEdit = async (prompt) => {
    try {
      const fullPrompt = await getSystemPromptById(prompt.id);
      setEditingPrompt(fullPrompt);
      setShowEditForm(true);
    } catch (error) {
      console.error('프롬프트 로드 실패:', error);
      alert('프롬프트를 로드할 수 없습니다.');
    }
  };

  const startDuplicate = (prompt) => {
    setDuplicatingPrompt(prompt);
    setDuplicateVersion(`${prompt.version}_copy`);
    setShowDuplicateForm(true);
  };

  if (accessDenied) {
    return <AdminAccessDenied />;
  }

  const actionButtons = (
    <button
      onClick={() => setShowCreateForm(true)}
      className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
    >
      <Plus size={16} className="mr-2" />
      새 버전 생성
    </button>
  );

  return (
    <AdminLayout
      title="시스템 프롬프트 관리"
      description="Latte is AI의 프롬프트 버전을 관리하고 성과를 분석합니다."
      icon={<Terminal className="h-8 w-8 text-blue-600" />}
      actions={actionButtons}
      isAdmin={isAdmin}
      loading={loading}
    >
      {/* 프롬프트 목록 */}
      <div className="bg-white shadow-lg rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  버전
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  설명
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  대상 그룹
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  컨설턴트 타입
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  세션 수
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  상태
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  생성일
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  작업
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {prompts.map((prompt) => (
                <tr key={prompt.id}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {prompt.version}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    <div className="max-w-xs truncate">
                      {prompt.description || '설명 없음'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {prompt.target_user_group}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                      prompt.consultant_type === 'latte-boss' 
                        ? 'bg-purple-100 text-purple-800' 
                        : 'bg-green-100 text-green-800'
                    }`}>
                      {prompt.consultant_type === 'latte-boss' ? '라떼부장' : '라떼매니저'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      {prompt.sessionCount || 0}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {prompt.is_active ? (
                      <span className="inline-flex items-center px-3 py-1 bg-green-100 text-green-800 rounded-full text-xs font-medium">
                        <CheckCircle className="h-3 w-3 mr-1" />
                        활성
                      </span>
                    ) : (
                      <button
                        onClick={() => handleActivatePrompt(prompt.id)}
                        className="inline-flex items-center px-3 py-1 bg-gray-100 text-gray-700 border border-gray-300 rounded-md text-xs hover:bg-green-50 hover:text-green-700 hover:border-green-300 transition-colors"
                      >
                        비활성 → 활성화하기
                      </button>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(prompt.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setSelectedPrompt(prompt)}
                        className="inline-flex items-center px-2 py-1 border border-blue-300 text-blue-700 bg-blue-50 rounded-md text-xs hover:bg-blue-100 transition-colors"
                      >
                        <Eye className="h-3 w-3 mr-1" />
                        내용 보기
                      </button>
                      <button
                        onClick={() => startEdit(prompt)}
                        className="inline-flex items-center px-2 py-1 border border-yellow-300 text-yellow-700 bg-yellow-50 rounded-md text-xs hover:bg-yellow-100 transition-colors"
                      >
                        <Edit className="h-3 w-3 mr-1" />
                        편집
                      </button>
                      <button
                        onClick={() => startDuplicate(prompt)}
                        className="inline-flex items-center px-2 py-1 border border-purple-300 text-purple-700 bg-purple-50 rounded-md text-xs hover:bg-purple-100 transition-colors"
                      >
                        <Copy className="h-3 w-3 mr-1" />
                        복제
                      </button>
                      {!prompt.is_active && (
                        <button
                          onClick={() => handleDeletePrompt(prompt.id, prompt.version)}
                          className="inline-flex items-center px-2 py-1 border border-red-300 text-red-700 bg-red-50 rounded-md text-xs hover:bg-red-100 transition-colors"
                        >
                          <Trash2 className="h-3 w-3 mr-1" />
                          삭제
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* 새 프롬프트 생성 모달 */}
      {showCreateForm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">새 프롬프트 버전 생성</h3>
              <form onSubmit={handleCreatePrompt} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">버전</label>
                  <input
                    type="text"
                    value={newPrompt.version}
                    onChange={(e) => setNewPrompt(prev => ({ ...prev, version: e.target.value }))}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="예: v1.1.0"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">설명</label>
                  <input
                    type="text"
                    value={newPrompt.description}
                    onChange={(e) => setNewPrompt(prev => ({ ...prev, description: e.target.value }))}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="이 버전의 변경사항을 설명해주세요"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">대상 사용자 그룹</label>
                  <select
                    value={newPrompt.targetUserGroup}
                    onChange={(e) => setNewPrompt(prev => ({ ...prev, targetUserGroup: e.target.value }))}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="all">전체 사용자</option>
                    <option value="general">일반 사용자</option>
                    <option value="admin">Admin 사용자</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">컨설턴트 타입</label>
                  <select
                    value={newPrompt.consultantType}
                    onChange={(e) => setNewPrompt(prev => ({ ...prev, consultantType: e.target.value }))}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="latte-manager">라떼매니저</option>
                    <option value="latte-boss">라떼부장</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">프롬프트 내용</label>
                  <textarea
                    value={newPrompt.content}
                    onChange={(e) => setNewPrompt(prev => ({ ...prev, content: e.target.value }))}
                    rows={20}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 font-mono text-sm"
                    placeholder="프롬프트 내용을 입력하세요..."
                    required
                  />
                </div>
                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => setShowCreateForm(false)}
                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                  >
                    취소
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                  >
                    생성하기
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* 프롬프트 편집 모달 */}
      {showEditForm && editingPrompt && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">프롬프트 편집: {editingPrompt.version}</h3>
                <button
                  onClick={() => {
                    setShowEditForm(false);
                    setEditingPrompt(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X size={20} />
                </button>
              </div>
              <form onSubmit={handleUpdatePrompt} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">버전</label>
                  <input
                    type="text"
                    value={editingPrompt.version}
                    onChange={(e) => setEditingPrompt(prev => ({ ...prev, version: e.target.value }))}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">설명</label>
                  <input
                    type="text"
                    value={editingPrompt.description || ''}
                    onChange={(e) => setEditingPrompt(prev => ({ ...prev, description: e.target.value }))}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">대상 사용자 그룹</label>
                  <select
                    value={editingPrompt.target_user_group}
                    onChange={(e) => setEditingPrompt(prev => ({ ...prev, target_user_group: e.target.value }))}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                  >
                    <option value="all">전체 사용자</option>
                    <option value="general">일반 사용자</option>
                    <option value="admin">Admin 사용자</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">컨설턴트 타입</label>
                  <select
                    value={editingPrompt.consultant_type || 'latte-manager'}
                    onChange={(e) => setEditingPrompt(prev => ({ ...prev, consultant_type: e.target.value }))}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                  >
                    <option value="latte-manager">라떼매니저</option>
                    <option value="latte-boss">라떼부장</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">프롬프트 내용</label>
                  <textarea
                    value={editingPrompt.content}
                    onChange={(e) => setEditingPrompt(prev => ({ ...prev, content: e.target.value }))}
                    rows={20}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 font-mono text-sm"
                    required
                  />
                </div>
                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => {
                      setShowEditForm(false);
                      setEditingPrompt(null);
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                  >
                    취소
                  </button>
                  <button
                    type="submit"
                    className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                  >
                    <Save size={16} className="mr-2" />
                    변경사항 저장
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* 프롬프트 복제 모달 */}
      {showDuplicateForm && duplicatingPrompt && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">프롬프트 복제</h3>
                <button
                  onClick={() => {
                    setShowDuplicateForm(false);
                    setDuplicatingPrompt(null);
                    setDuplicateVersion('');
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X size={20} />
                </button>
              </div>
              <form onSubmit={handleDuplicatePrompt} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">원본 버전</label>
                  <input
                    type="text"
                    value={duplicatingPrompt.version}
                    disabled
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 bg-gray-100"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">새 버전</label>
                  <input
                    type="text"
                    value={duplicateVersion}
                    onChange={(e) => setDuplicateVersion(e.target.value)}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                    placeholder="예: v1.1.0_copy"
                    required
                  />
                </div>
                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => {
                      setShowDuplicateForm(false);
                      setDuplicatingPrompt(null);
                      setDuplicateVersion('');
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                  >
                    취소
                  </button>
                  <button
                    type="submit"
                    className="inline-flex items-center px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700"
                  >
                    <Copy size={16} className="mr-2" />
                    복제하기
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* 프롬프트 내용 보기 모달 */}
      {selectedPrompt && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-4 bottom-4 mx-auto p-6 border w-11/12 max-w-6xl shadow-lg rounded-md bg-white" style={{ minHeight: 'calc(100vh - 2rem)' }}>
            <div className="h-full flex flex-col">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-medium text-gray-900">
                  프롬프트 {selectedPrompt.version} 내용
                </h3>
                <button
                  onClick={() => setSelectedPrompt(null)}
                  className="text-gray-400 hover:text-gray-600 p-1"
                >
                  <X size={24} />
                </button>
              </div>
              
              {/* 프롬프트 메타 정보 */}
              <div className="mb-4 p-4 bg-gray-50 rounded-lg">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <span className="font-medium text-gray-700">버전:</span>
                    <span className="ml-2 text-gray-900">{selectedPrompt.version}</span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">대상 그룹:</span>
                    <span className="ml-2 text-gray-900">{selectedPrompt.target_user_group}</span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">세션 수:</span>
                    <span className="ml-2 text-gray-900">{selectedPrompt.sessionCount || 0}</span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">상태:</span>
                    <span className={`ml-2 ${selectedPrompt.is_active ? 'text-green-600 font-medium' : 'text-gray-600'}`}>
                      {selectedPrompt.is_active ? '✓ 활성' : '비활성'}
                    </span>
                  </div>
                </div>
                {selectedPrompt.description && (
                  <div className="mt-3">
                    <span className="font-medium text-gray-700">설명:</span>
                    <span className="ml-2 text-gray-900">{selectedPrompt.description}</span>
                  </div>
                )}
              </div>
              
              {/* 프롬프트 내용 */}
              <div className="flex-1 bg-gray-50 rounded-lg p-4 overflow-hidden flex flex-col">
                <h4 className="text-lg font-medium text-gray-900 mb-3">프롬프트 내용</h4>
                <div className="flex-1 overflow-y-auto bg-white rounded border p-4">
                  <pre className="whitespace-pre-wrap text-sm text-gray-800 leading-relaxed font-mono">
                    {selectedPrompt.content}
                  </pre>
                  {/* 새로운 메시지 배열 형식 예시 표시 */}
                  {selectedPrompt.content.includes('"message":') && (
                    <div className="mt-6 p-4 bg-blue-50 rounded-md border border-blue-200">
                      <h5 className="font-medium text-blue-900 mb-2">📌 새로운 응답 형식 안내</h5>
                      <p className="text-sm text-blue-800 mb-2">
                        이제 AI 응답의 message 필드가 배열 형식을 지원합니다. 각 문자열이 개별 말풍선으로 표시됩니다.
                      </p>
                      <pre className="bg-white p-3 rounded border border-blue-300 text-xs font-mono">
{`{
  "message": [
    "첫 번째 말풍선의 내용",
    "두 번째 말풍선의 내용", 
    "마지막 말풍선 (보통 질문)"
  ],
  "updatedExperiences": [...],
  "extractedOtherInfo": [...],
  "nextStep": "2"
}`}
                      </pre>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminPromptManager;
